import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Building2, ExternalLink, ArrowRight, Check, Clock, CirclePlus, Loader } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';

const Integrations = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  
  // State for integration statuses
  const [integrationStatuses, setIntegrationStatuses] = useState({
    curbside: 'not_started'
  });
  
  const [loading, setLoading] = useState(true);
  
  // Fetch integration statuses
  useEffect(() => {
    const fetchIntegrationStatuses = async () => {
      try {
        setLoading(true);
        
        // Get current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        
        if (user) {
          // Check for Curbside settings
          const { data, error } = await supabase
            .from('curbside_settings')
            .select('integration_status')
            .eq('account_id', user.id)
            .maybeSingle();
            
          if (error && error.code !== 'PGRST116') throw error;
          
          if (data) {
            setIntegrationStatuses(prev => ({
              ...prev,
              curbside: data.integration_status || 'not_started'
            }));
          }
        }
      } catch (err) {
        console.error('Error fetching integration statuses:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (location.pathname === '/connect/apps' || location.pathname === '/integrations') {
      fetchIntegrationStatuses();
    }
  }, [location.pathname]);
  
  // Function to get status display info
  const getStatusDisplay = (integrationId) => {
    const status = integrationStatuses[integrationId];
    
    switch(status) {
      case 'complete':
        return {
          label: 'Connected',
          icon: Check,
          className: 'bg-gray-900 text-white'
        };
      case 'in_progress':
        return {
          label: 'Setting Up',
          icon: Clock,
          className: 'bg-gray-100 text-gray-700 border border-gray-200'
        };
      case 'not_started':
      default:
        return {
          label: 'Connect',
          icon: CirclePlus,
          className: 'bg-gray-50 text-gray-700 border border-gray-200'
        };
    }
  };
  
  // List of available integrations
  const integrations = [
    {
      id: 'curbside',
      name: 'Curbside',
      description: 'Integrate with Curbside POS for laundry services',
      path: '/connect/apps/curbside',
      icon: Building2
    },
    // Add more integrations here as they become available
  ];
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.2,
        ease: [0, 0, 0.2, 1]
      }
    },
    hover: { 
      y: -4,
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.02)",
      transition: { 
        duration: 0.2,
        ease: [0, 0, 0.2, 1]
      }
    }
  };
  
  // If we're at the base route, show the list of integrations
  if (location.pathname === '/connect/apps' || location.pathname === '/integrations') {
    // Instead of showing a loading state, simply render the page
    // The status will update when data arrives
    return (
      <motion.div 
        className="min-h-screen bg-gray-50 flex flex-col items-center"
        variants={pageVariants}
        initial="initial"
        animate="animate"
      >
        <div className="w-full max-w-4xl px-4">
          <PageHeader
            title="Apps"
            description="Connect your account with third-party services"
            icon={ExternalLink}
          />
          
          <main className="w-full mx-auto py-8">
            {!loading ? (
              <>
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                  variants={pageVariants}
                >
                  {integrations.map((integration) => {
                    const statusDisplay = getStatusDisplay(integration.id);
                    const StatusIcon = statusDisplay.icon;
                    
                    return (
                      <motion.div
                        key={integration.id}
                        variants={cardVariants}
                        whileHover="hover"
                        className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-200"
                      >
                        <Link 
                          to={integration.path} 
                          className="block h-full"
                        >
                          <div className="p-6 flex flex-col h-full">
                            <div className="flex items-start justify-between mb-4">
                              <div className="bg-gray-50 p-2 rounded-full">
                                <integration.icon className="w-5 h-5 text-gray-700" />
                              </div>
                              <div className={`flex items-center text-xs font-medium px-3 py-1 rounded-full transition-colors ${statusDisplay.className}`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                <span>{statusDisplay.label}</span>
                              </div>
                            </div>
                            
                            <h3 className="text-lg font-medium text-gray-900 mb-2">{integration.name}</h3>
                            <p className="text-gray-600 text-sm mb-6 flex-grow">{integration.description}</p>
                            
                            <div className="flex items-center text-gray-900 font-medium text-sm mt-auto group">
                              <span>{
                                integrationStatuses[integration.id] === 'complete' ? 'Manage' : 
                                integrationStatuses[integration.id] === 'in_progress' ? 'View Progress' : 
                                'Setup'
                              }</span>
                              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    );
                  })}
                </motion.div>
                
                {/* Empty state if no integrations */}
                {integrations.length === 0 && (
                  <motion.div 
                    variants={cardVariants}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center"
                  >
                    <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <ExternalLink className="w-6 h-6 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No integrations available</h3>
                    <p className="text-gray-600 text-sm">
                      We're working on adding integrations. Check back soon.
                    </p>
                  </motion.div>
                )}
              </>
            ) : (
              <div className="h-[200px]"></div> /* Empty space as placeholder while loading */
            )}
          </main>
        </div>
      </motion.div>
    );
  }
  
  // Otherwise, render the specific integration view through Outlet
  return <Outlet />;
};

export default Integrations; 